import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Layers,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  FileQuestion,
  X,
  Eraser,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminEnv, ReadOnlyBanner } from "../components/admin/EnvContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_FALLBACK = ["math", "science", "english", "hindi"];
const SUBJECT_LABELS = { english: "English Grammar", hindi: "Hindi Vyakaran" };
const subjectLabel = (s) =>
  SUBJECT_LABELS[(s || "").toLowerCase()] ||
  ((s || "").charAt(0).toUpperCase() + (s || "").slice(1));
const DIFF_COLORS = {
  easy: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  hard: "bg-red-100 text-red-700",
};
const UNASSIGNED = "__unassigned__";

// Options may be stored as an array OR a legacy dict {A:..,B:..}; always work with an array.
const optionsToArray = (opts) => {
  if (Array.isArray(opts)) return opts;
  if (opts && typeof opts === "object") return Object.values(opts);
  return [];
};

const req = async (url, opts = {}) => {
  const res = await fetch(`${API}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
};

export default function QuestionBankAdmin() {
  const { isProduction: readOnly } = useAdminEnv();
  const [scope, setScope] = useState(null); // {grades:[], subjects:[]} for CM, null for admin
  const [board, setBoard] = useState("cbse");
  const [grade, setGrade] = useState("6");
  const [subject, setSubject] = useState("math");
  const [subjects, setSubjects] = useState(SUBJECT_FALLBACK);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  // filters
  const [topicFilter, setTopicFilter] = useState(""); // "" = all topics
  const [subtopicFilter, setSubtopicFilter] = useState(""); // "" = all sub-topics
  const [difficulty, setDifficulty] = useState(""); // "" = all

  // question cache + selection
  const [cache, setCache] = useState({}); // topicId -> questions[]
  const [openTopics, setOpenTopics] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [editQ, setEditQ] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orphanBusy, setOrphanBusy] = useState(false);

  // AI model + per-sub-topic MCQ generation jobs
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("gemini-3.1-pro");
  const [genJobs, setGenJobs] = useState({}); // `${topicId}:${scId}` -> job

  const canGenerate = !readOnly && !scope; // admins only (CMs cannot call generate endpoint)

  // role + scope
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/auth/me`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        const admin = data.admin;
        if (admin && admin.role === "content_manager") {
          const sc = admin.content_scope || { grades: [], subjects: [] };
          setScope(sc);
          if (sc.grades?.length) setGrade(String(sc.grades[0]));
          if (sc.subjects?.length) {
            setSubject(String(sc.subjects[0]).toLowerCase());
            setSubjects(sc.subjects.map((s) => String(s).toLowerCase()));
          }
        }
      } catch {
        /* ignore */
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const gradeOptions = scope?.grades?.length ? scope.grades.map(String) : ["5", "6", "7", "8"];

  const load = useCallback(async () => {
    setLoading(true);
    setCache({});
    setSelected(new Set());
    setOpenTopics([]);
    setTopicFilter("");
    setSubtopicFilter("");
    try {
      const data = await req(
        `/admin/curriculum/content/detail?board=${board}&grade=${grade}&subject=${encodeURIComponent(subject)}`
      );
      setTopics(data.topics || []);
      if (data.subjects?.length) {
        const subs = scope?.subjects?.length
          ? data.subjects.filter((s) => scope.subjects.map((x) => x.toLowerCase()).includes(s))
          : data.subjects;
        if (subs.length) setSubjects(subs);
      }
    } catch (e) {
      toast.error(e.message || "Could not load content");
    } finally {
      setLoading(false);
    }
  }, [board, grade, subject, scope]);

  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const loadTopic = useCallback(async (topicId) => {
    try {
      const data = await req(`/admin/curriculum/topic/${topicId}/questions`);
      setCache((prev) => ({ ...prev, [topicId]: data.questions || [] }));
    } catch (e) {
      toast.error(e.message || "Could not load questions");
    }
  }, []);

  // Load available AI models for generation (admins only)
  useEffect(() => {
    if (!canGenerate) return;
    (async () => {
      try {
        const d = await req(`/admin/curriculum/content/models?task=questions`);
        setModels(d.models || []);
        if (d.default) setModel(d.default);
      } catch {
        /* ignore */
      }
    })();
  }, [canGenerate]);

  // Poll running MCQ-generation jobs
  useEffect(() => {
    const running = Object.entries(genJobs).filter(([, j]) => j.status === "running");
    if (!running.length) return;
    const timer = setInterval(async () => {
      for (const [key, j] of running) {
        try {
          const data = await req(`/admin/curriculum/content/generate-questions/${j.id}`);
          setGenJobs((prev) => ({ ...prev, [key]: { ...prev[key], ...data.job } }));
          if (data.job.status === "done") {
            toast.success(`Generated ${data.job.generated} MCQs for "${data.job.topic}"`);
            loadTopic(data.job.topic_id);
          } else if (data.job.status === "failed") {
            toast.error(`Generation failed: ${data.job.error || "unknown error"}`);
          }
        } catch {
          /* ignore transient poll errors */
        }
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [genJobs, loadTopic]);

  const startGen = useCallback(
    async (topicId, scId, count) => {
      const key = `${topicId}:${scId}`;
      if (genJobs[key]?.status === "running") return;
      const total = Math.max(1, Math.min(100, parseInt(count, 10) || 30));
      // Keep the 40/40/20 easy/medium/hard mix for whatever count is entered.
      const easy = Math.round(total * 0.4);
      const medium = Math.round(total * 0.4);
      const hard = total - easy - medium;
      try {
        const data = await req(`/admin/curriculum/content/generate-questions`, {
          method: "POST",
          body: JSON.stringify({
            topic_id: topicId,
            subconcept_id: scId,
            model,
            easy,
            medium,
            hard,
            mcq_only: true,
          }),
        });
        setGenJobs((prev) => ({ ...prev, [key]: data.job }));
        toast.info(`Generating ${data.job.target} MCQs in the background…`);
      } catch (e) {
        toast.error(e.message || "Could not start generation");
      }
    },
    [genJobs, model]
  );

  // When a specific topic is chosen, open + load it
  useEffect(() => {
    setSubtopicFilter("");
    if (topicFilter) {
      setOpenTopics([topicFilter]);
      if (!cache[topicFilter]) loadTopic(topicFilter);
    }
  }, [topicFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleTopics = useMemo(
    () => (topicFilter ? topics.filter((t) => t.id === topicFilter) : topics),
    [topics, topicFilter]
  );

  const selectedTopicObj = useMemo(
    () => topics.find((t) => t.id === topicFilter),
    [topics, topicFilter]
  );

  // filter predicate for a single question within a topic
  const matchQuestion = useCallback(
    (q, topic) => {
      if (difficulty && (q.difficulty || "").toLowerCase() !== difficulty) return false;
      if (subtopicFilter) {
        const knownIds = new Set((topic.concept_details || []).map((c) => c.id));
        if (subtopicFilter === UNASSIGNED)
          return !q.subconcept_id || !knownIds.has(q.subconcept_id);
        return q.subconcept_id === subtopicFilter;
      }
      return true;
    },
    [difficulty, subtopicFilter]
  );

  const getDoc = useCallback(
    (id) => {
      for (const arr of Object.values(cache)) {
        const f = arr.find((q) => q.id === id);
        if (f) return f;
      }
      return null;
    },
    [cache]
  );

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const visibleQuestionIds = useCallback(() => {
    const ids = [];
    visibleTopics.forEach((topic) => {
      (cache[topic.id] || []).forEach((q) => {
        if (matchQuestion(q, topic)) ids.push(q.id);
      });
    });
    return ids;
  }, [visibleTopics, cache, matchQuestion]);

  const selectAllInView = () => {
    const ids = visibleQuestionIds();
    if (!ids.length) {
      toast.info("Expand a topic first, then select.");
      return;
    }
    setSelected(new Set(ids));
  };

  const restoreQuestions = async (docs) => {
    try {
      await req(`/admin/curriculum/questions/bulk-restore`, {
        method: "POST",
        body: JSON.stringify({ questions: docs }),
      });
      setCache((prev) => {
        const n = { ...prev };
        docs.forEach((d) => {
          const t = d.topic_id;
          if (n[t] && !n[t].some((x) => x.id === d.id)) n[t] = [...n[t], d];
        });
        return n;
      });
      toast.success(`Restored ${docs.length} question${docs.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.message || "Restore failed");
    }
  };

  const deleteQuestions = async (ids) => {
    if (!ids.length) return;
    const docs = ids.map(getDoc).filter(Boolean);
    setDeleting(true);
    try {
      const res = await req(`/admin/curriculum/questions/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      });
      setCache((prev) => {
        const n = {};
        for (const [tid, arr] of Object.entries(prev)) n[tid] = arr.filter((q) => !ids.includes(q.id));
        return n;
      });
      setSelected(new Set());
      const n = res.deleted ?? ids.length;
      toast.success(`${n} question${n > 1 ? "s" : ""} deleted`, {
        duration: 8000,
        action: { label: "Undo", onClick: () => restoreQuestions(docs) },
      });
    } catch (e) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const cleanupOrphans = async () => {
    setOrphanBusy(true);
    try {
      const prev = await req(`/admin/curriculum/questions/orphans`);
      if (!prev.total) {
        toast.success("No orphaned questions found 🎉");
        return;
      }
      if (
        !window.confirm(
          `Found ${prev.total} orphaned question(s) whose topic or sub-topic was removed ` +
            `(${prev.stale_subconcept} from removed sub-topics, ${prev.missing_topic} from removed topics). ` +
            `Permanently delete them?`
        )
      )
        return;
      const res = await req(`/admin/curriculum/questions/orphans/cleanup`, { method: "POST" });
      toast.success(`Removed ${res.deleted} orphaned question${res.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.message || "Cleanup failed");
    } finally {
      setOrphanBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow">
          <FileQuestion className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Question Bank</h1>
          <p className="text-sm text-slate-500">
            {readOnly
              ? "Browse by topic and sub-topic. Viewing and filters only — questions are authored in Preview and promoted here."
              : "Browse by topic and sub-topic. Edit, delete or bulk-delete questions. Changes are live."}
          </p>
        </div>
      </div>

      {readOnly && <ReadOnlyBanner feature="The Question Bank" />}

      {/* Selectors + filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-lg p-4">
        <Field label="Board">
          <select className="qb-select" value={board} onChange={(e) => setBoard(e.target.value)} data-testid="qb-board-select">
            <option value="cbse">CBSE</option>
            <option value="ib">IB</option>
          </select>
        </Field>
        <Field label="Class">
          <select className="qb-select" value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="qb-grade-select">
            {gradeOptions.map((g) => (
              <option key={g} value={g}>Class {g}</option>
            ))}
          </select>
        </Field>
        <Field label="Subject">
          <select className="qb-select" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="qb-subject-select">
            {subjects.map((s) => (
              <option key={s} value={s}>{subjectLabel(s)}</option>
            ))}
          </select>
        </Field>

        <div className="w-px h-10 bg-slate-200 mx-1 hidden sm:block" />

        <Field label="Topic">
          <select className="qb-select" value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} data-testid="filter-topic-select">
            <option value="">All topics</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>{t.topic}</option>
            ))}
          </select>
        </Field>
        <Field label="Sub-topic">
          <select
            className="qb-select disabled:opacity-50"
            value={subtopicFilter}
            onChange={(e) => setSubtopicFilter(e.target.value)}
            disabled={!topicFilter}
            title={topicFilter ? "" : "Pick a topic first"}
            data-testid="filter-subtopic-select"
          >
            <option value="">All sub-topics</option>
            {(selectedTopicObj?.concept_details || []).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
            <option value={UNASSIGNED}>Unassigned</option>
          </select>
        </Field>
        <Field label="Difficulty">
          <select className="qb-select" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="filter-difficulty-select">
            <option value="">All levels</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </Field>

        {canGenerate && (
          <Field label="AI model (generation)">
            <select className="qb-select" value={model} onChange={(e) => setModel(e.target.value)} data-testid="qb-model-select">
              {models.length === 0 && <option value={model}>{model}</option>}
              {models.map((m) => (
                <option key={m.id || m} value={m.id || m}>{m.label || m.id || m}</option>
              ))}
            </select>
          </Field>
        )}

        <Button variant="outline" onClick={load} disabled={loading} data-testid="qb-reload-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Reload
        </Button>
        {!readOnly && (
        <Button
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={cleanupOrphans}
          disabled={orphanBusy}
          title="Delete questions whose topic or sub-topic has been removed"
          data-testid="qb-cleanup-orphans-btn"
        >
          {orphanBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eraser className="h-4 w-4 mr-1" />}
          Remove orphaned questions
        </Button>
        )}
      </div>

      {/* Bulk selection bar */}
      {selected.size > 0 && (
        <div
          className="sticky top-2 z-30 flex items-center justify-between gap-3 bg-indigo-600 text-white rounded-lg px-4 py-2 shadow-lg"
          data-testid="qb-bulk-bar"
        >
          <span className="text-sm font-medium" data-testid="qb-bulk-selected-count">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={selectAllInView} data-testid="qb-select-all-btn">
              Select all in view
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-indigo-500"
              onClick={() => setSelected(new Set())}
              data-testid="qb-clear-selection-btn"
            >
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
            <Button
              size="sm"
              className="bg-red-500 hover:bg-red-600"
              onClick={() => deleteQuestions([...selected])}
              disabled={deleting}
              data-testid="qb-bulk-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading topics…
        </div>
      ) : visibleTopics.length === 0 ? (
        <p className="text-sm text-slate-500">No topics found for this selection.</p>
      ) : (
        <Accordion
          type="multiple"
          className="space-y-3"
          value={openTopics}
          onValueChange={(vals) => {
            setOpenTopics(vals);
            vals.forEach((id) => {
              if (!cache[id]) loadTopic(id);
            });
          }}
        >
          {visibleTopics.map((topic) => (
            <TopicQuestions
              key={topic.id}
              topic={topic}
              questions={cache[topic.id]}
              difficulty={difficulty}
              subtopicFilter={subtopicFilter}
              selected={selected}
              readOnly={readOnly}
              genJobs={genJobs}
              onGenerate={canGenerate ? startGen : null}
              onToggleSelect={toggleSelect}
              onEditQ={setEditQ}
              onDeleteOne={(id) => deleteQuestions([id])}
            />
          ))}
        </Accordion>
      )}

      {editQ && (
        <QuestionEditModal
          question={editQ}
          onClose={() => setEditQ(null)}
          onSaved={() => {
            const tid = editQ.topic_id;
            setEditQ(null);
            if (tid) loadTopic(tid);
          }}
        />
      )}

      <style>{`.qb-select{height:2.5rem;border-radius:0.375rem;border:1px solid hsl(var(--input));background:hsl(var(--background));padding:0 0.75rem;font-size:0.875rem}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function TopicQuestions({ topic, questions, difficulty, subtopicFilter, selected, onToggleSelect, onEditQ, onDeleteOne, readOnly = false, genJobs = {}, onGenerate = null }) {
  const cds = topic.concept_details || [];
  const loaded = questions !== undefined;
  const list = questions || [];
  const [openSubs, setOpenSubs] = useState(() => new Set());
  const [genCounts, setGenCounts] = useState({}); // per sub-topic id -> desired MCQ count
  const getCount = (id) => (genCounts[id] === undefined ? 30 : genCounts[id]);
  const setCount = (id, v) => setGenCounts((p) => ({ ...p, [id]: v }));
  const toggleSub = (id) =>
    setOpenSubs((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  // When a filter is active, force sections open so results are visible.
  const forceOpen = !!subtopicFilter || !!difficulty;

  const matchDiff = (q) => !difficulty || (q.difficulty || "").toLowerCase() === difficulty;

  // group by subconcept_id
  const byId = {};
  list.forEach((q) => {
    const key = q.subconcept_id || UNASSIGNED;
    (byId[key] = byId[key] || []).push(q);
  });
  const knownIds = new Set(cds.map((c) => c.id));
  const unassigned = [];
  Object.keys(byId).forEach((k) => {
    if (k === UNASSIGNED || !knownIds.has(k)) unassigned.push(...byId[k]);
  });

  const displayedCount = loaded ? list.filter(matchDiff).length : topic.question_count;

  const renderQ = (q) => {
    const isSel = selected.has(q.id);
    return (
      <div
        key={q.id}
        className={`border rounded-md p-2 flex items-start gap-2 bg-white ${isSel ? "ring-2 ring-indigo-400" : ""}`}
        data-testid={`qb-question-${q.id}`}
      >
        {!readOnly && (
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-indigo-600 cursor-pointer"
          checked={isSel}
          onChange={() => onToggleSelect(q.id)}
          data-testid={`qb-select-q-${q.id}`}
        />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge className={DIFF_COLORS[(q.difficulty || "").toLowerCase()] || ""}>{q.difficulty || "—"}</Badge>
            <span className="text-xs text-slate-400">{q.qtype}</span>
          </div>
          <p className="text-sm mt-1">{q.stem}</p>
          {optionsToArray(q.options).length > 0 && (
            <p className="text-xs text-slate-500 mt-0.5">{optionsToArray(q.options).join(" · ")}</p>
          )}
          <p className="text-xs text-emerald-700 mt-0.5">Answer: {q.answer}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          {!readOnly && (
          <Button size="sm" variant="ghost" onClick={() => onEditQ(q)} data-testid={`qb-edit-q-${q.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          )}
          {!readOnly && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDeleteOne(q.id)} data-testid={`qb-del-q-${q.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          )}
        </div>
      </div>
    );
  };

  const sectionVisible = (id) => !subtopicFilter || subtopicFilter === id;

  return (
    <AccordionItem value={topic.id} className="border rounded-lg bg-white px-4" data-testid={`qb-topic-${topic.id}`}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-2 text-left">
          <span className="font-semibold">{topic.topic}</span>
          <Badge variant="secondary">{cds.length} sub-topics</Badge>
          <Badge className="bg-indigo-100 text-indigo-700">{displayedCount} Qs</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4">
        {!loaded ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading questions…
          </div>
        ) : (
          <div className="space-y-4">
            {cds.map((cd) => {
              if (!sectionVisible(cd.id)) return null;
              const items = (byId[cd.id] || []).filter(matchDiff);
              if (difficulty && items.length === 0) return null;
              const isOpen = forceOpen || openSubs.has(cd.id);
              return (
                <div key={cd.id} data-testid={`qb-subtopic-${cd.id}`} className="border border-slate-100 rounded-md">
                  <div className="flex items-center gap-2 px-2 py-2">
                    <button
                      type="button"
                      onClick={() => toggleSub(cd.id)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                      data-testid={`qb-subtopic-toggle-${cd.id}`}
                      aria-expanded={isOpen}
                    >
                      <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      <Layers className="h-4 w-4 text-indigo-500 shrink-0" />
                      <p className="text-sm font-semibold text-slate-700 truncate">{cd.title}</p>
                      <Badge variant="outline" className="text-xs shrink-0">{items.length} Qs</Badge>
                    </button>
                    {onGenerate && (() => {
                      const job = genJobs[`${topic.id}:${cd.id}`];
                      return job?.status === "running" ? (
                        <Badge className="bg-blue-100 text-blue-700 shrink-0">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          {job.generated || 0}/{job.target}
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number"
                            min={1}
                            max={100}
                            value={getCount(cd.id)}
                            onChange={(e) => setCount(cd.id, e.target.value)}
                            onBlur={(e) => setCount(cd.id, Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 30)))}
                            className="qb-select h-7 w-16 px-2 py-0 text-sm"
                            data-testid={`qb-gen-count-${cd.id}`}
                            title="How many MCQs to generate (40% easy / 40% medium / 20% hard)"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={() => onGenerate(topic.id, cd.id, getCount(cd.id))}
                            data-testid={`qb-gen-sc-${cd.id}`}
                            title={`Generate ${getCount(cd.id)} MCQs (40% easy / 40% medium / 20% hard)`}
                          >
                            <Sparkles className="h-3.5 w-3.5 mr-1" /> Generate
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                  {isOpen && (
                    items.length === 0 ? (
                      <p className="text-xs text-slate-400 pl-8 pb-2">No questions for this sub-topic yet.</p>
                    ) : (
                      <div className="space-y-2 pl-8 pr-2 pb-3">{items.map(renderQ)}</div>
                    )
                  )}
                </div>
              );
            })}
            {sectionVisible(UNASSIGNED) && (() => {
              const items = unassigned.filter(matchDiff);
              if (!items.length) return null;
              const isOpen = forceOpen || openSubs.has(UNASSIGNED);
              return (
                <div data-testid={`qb-subtopic-unassigned-${topic.id}`} className="border border-slate-100 rounded-md">
                  <button
                    type="button"
                    onClick={() => toggleSub(UNASSIGNED)}
                    className="flex items-center gap-2 w-full text-left px-2 py-2 hover:opacity-80 transition-opacity"
                    data-testid={`qb-subtopic-unassigned-toggle-${topic.id}`}
                    aria-expanded={isOpen}
                  >
                    <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <Layers className="h-4 w-4 text-slate-400 shrink-0" />
                    <p className="text-sm font-semibold text-slate-500">Unassigned</p>
                    <Badge variant="outline" className="text-xs shrink-0">{items.length} Qs</Badge>
                  </button>
                  {isOpen && <div className="space-y-2 pl-8 pr-2 pb-3">{items.map(renderQ)}</div>}
                </div>
              );
            })()}
            {loaded && list.filter(matchDiff).length === 0 && (
              <p className="text-sm text-slate-500">No questions match the current filters.</p>
            )}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function QuestionEditModal({ question, onClose, onSaved }) {
  const [q, setQ] = useState({
    stem: question.stem || "",
    options: optionsToArray(question.options).join("\n"),
    answer: question.answer || "",
    explanation: question.explanation || "",
    hint: question.hint || "",
    difficulty: question.difficulty || "medium",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const options = q.options.split("\n").map((o) => o.trim()).filter(Boolean);
      await req(`/admin/curriculum/question/${question.id}`, {
        method: "PUT",
        body: JSON.stringify({
          stem: q.stem,
          options,
          answer: q.answer,
          acceptable_answers: [q.answer],
          explanation: q.explanation,
          hint: q.hint,
          difficulty: q.difficulty,
        }),
      });
      toast.success("Question saved");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qb-edit-q-modal">
        <DialogTitle>Edit question</DialogTitle>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Stem</label>
            <Textarea value={q.stem} onChange={(e) => setQ({ ...q, stem: e.target.value })} rows={3} data-testid="qb-q-stem-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Options (one per line, MCQ)</label>
            <Textarea value={q.options} onChange={(e) => setQ({ ...q, options: e.target.value })} rows={4} data-testid="qb-q-options-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Answer</label>
              <Input value={q.answer} onChange={(e) => setQ({ ...q, answer: e.target.value })} data-testid="qb-q-answer-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Difficulty</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3"
                value={q.difficulty}
                onChange={(e) => setQ({ ...q, difficulty: e.target.value })}
                data-testid="qb-q-difficulty-select"
              >
                <option value="easy">easy</option>
                <option value="medium">medium</option>
                <option value="hard">hard</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Explanation</label>
            <Input value={q.explanation} onChange={(e) => setQ({ ...q, explanation: e.target.value })} data-testid="qb-q-explanation-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Hint</label>
            <Input value={q.hint} onChange={(e) => setQ({ ...q, hint: e.target.value })} data-testid="qb-q-hint-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="qb-q-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
